/**
 * Infinite scroll with vehicle/universal separation.
 * - Vehicles stay in the main grid.
 * - Universals are appended into a second "bucket" grid with identical classes.
 *
 * Requirements:
 *  - Each product card has data-fitment="vehicle" or "universal" on an element inside .js-pagination__item
 *    (we added this in card-product.liquid).
 */

if (!customElements.get('infinite-scroll')) {
  class InfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      // Core nodes
      this.paginationContainer = this.querySelector('.js-pagination');
      this.paginationItems = this.querySelectorAll('.js-pagination__item');
      this.nextButton = this.querySelector('.js-pagination__next-button');

      // Container that directly holds .js-pagination__item nodes
      this.itemsContainer = this._findItemsContainer();

      if (!this.paginationContainer || !this.itemsContainer || !this.paginationItems.length || !this.nextButton?.hasAttribute('href')) {
        return;
      }

      // Ensure a dedicated bucket for universals (same classes as items container)
      this._ensureUniversalBucket();

      // Partition any items already on the page (light-touch move only universals)
      this._partitionExistingItemsIntoBuckets();

      this.bindListeners();
    }

    destroy() {
      clearInterval(this.nextButtonTimer);
      this.nextButton?.removeEventListener('click', this.clickHandler);
      this.intersectionObserver?.disconnect();
    }

    bindListeners() {
      this.clickHandler = this.onNextButtonClick.bind(this);
      this.nextButton.addEventListener('click', this.clickHandler);

      if (this.dataset.infiniteScrollEnabled === 'true') {
        this.intersectionObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.fetchNextItems();
              this.nextButtonTimer = setInterval(this.fetchNextItems.bind(this), 1500);
            } else {
              clearInterval(this.nextButtonTimer);
            }
          });
        }, { rootMargin: `${window.innerHeight}px 0px` });

        this.intersectionObserver.observe(this.paginationContainer);
      }
    }

    onNextButtonClick(event) {
      event.preventDefault();
      this.fetchNextItems();
    }

    async fetchNextItems() {
      if (this.fetchInProgress) return;

      const originalButtonText = this.nextButton.textContent;
      this.fetchInProgress = true;
      this.nextButton.setAttribute('aria-disabled', 'true');
      this.nextButton.textContent = (window.strings?.general?.pagination?.loadingText) || 'Loading…';

      try {
        const response = await fetch(this.nextButton.href);
        if (!response.ok) throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);

        const template = document.createElement('template');
        template.innerHTML = await response.text();

        // Append the new items into the correct bucket (vehicle vs universal)
        const addedCount = this._appendBatchedItemsIntoBuckets(template.content);

        // Re-run any theme initializers after DOM changes
        setTimeout(() => {
          // These are theme-specific helpers; guard each one
          try { if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(); } catch(e){}
          try { window.renderSelects?.(); } catch(e){}
          try { window.loadDesktopOnlyTemplates?.(); } catch(e){}
          try { window.stickersReinit?.(); } catch(e){}

          // Focus first newly added card link if "show more" style requires it
          if (document.body.classList.contains('tab-active') && window.theme?.settings?.paginationLoadType === 'show_more') {
            const firstNew = this.querySelectorAll('.js-pagination__item a')[this.paginationItems.length - addedCount];
            firstNew?.focus();
          }
        }, 300);

        // Update pagination controls
        this._updatePaginationControlsFromTemplate(template.content);

      } catch (error) {
        console.error('[infinite-scroll] Failed to load new items:', error);
      } finally {
        this.fetchInProgress = false;
        this.nextButton.removeAttribute('aria-disabled');
        this.nextButton.textContent = originalButtonText;
      }
    }

    /* ----------------- Helpers ----------------- */

    _findItemsContainer() {
      // Find the closest common parent that directly contains .js-pagination__item nodes
      const anyItem = this.querySelector('.js-pagination__item');
      if (!anyItem) return null;
      let parent = anyItem.parentElement;
      while (parent && parent !== this) {
        const hasOnlyItemsOrRelated = parent.querySelector('.js-pagination__item');
        const hasPagination = parent.querySelector('.js-pagination');
        // We want the parent that has items as direct children at scale
        if (hasOnlyItemsOrRelated) return parent;
        parent = parent.parentElement;
      }
      return anyItem.parentElement || null;
    }

    _ensureUniversalBucket() {
      if (this.universalBucket && document.body.contains(this.universalBucket)) return;

      // Create a sibling container with identical classes to the main items container
      const bucket = document.createElement('div');
      bucket.className = this.itemsContainer.className; // copy grid classes so layout remains identical
      bucket.setAttribute('data-fitment-bucket', 'universal');

      // Insert bucket right before pagination, so it always renders after vehicles
      if (this.paginationContainer) {
        this.paginationContainer.before(bucket);
      } else {
        this.appendChild(bucket);
      }
      this.universalBucket = bucket;
    }

    _partitionExistingItemsIntoBuckets() {
      // Move only the universal items into the bucket; leave vehicles untouched
      const items = Array.from(this.itemsContainer.querySelectorAll(':scope > .js-pagination__item'));
      if (!items.length) return;
      this._ensureUniversalBucket();

      for (const item of items) {
        if (this._isUniversalItem(item)) {
          this.universalBucket.appendChild(item); // move node as-is (listeners remain)
        }
      }

      // refresh reference
      this.paginationItems = this.querySelectorAll('.js-pagination__item');
    }

    _appendBatchedItemsIntoBuckets(fragment) {
      // Collect all incoming items from this page
      const newItems = Array.from(fragment.querySelectorAll('.js-pagination__item'));
      if (!newItems.length) return 0;

      this._ensureUniversalBucket();

      // Partition and append without touching already-rendered items
      for (const item of newItems) {
        const isUniversal = this._isUniversalItem(item);
        (isUniversal ? this.universalBucket : this.itemsContainer).appendChild(item);
      }

      // how many were added overall
      this.paginationItems = this.querySelectorAll('.js-pagination__item');
      return newItems.length;
    }

    _isUniversalItem(itemEl) {
      const card = itemEl.querySelector('[data-fitment]');
      const fit = card?.getAttribute('data-fitment') || 'vehicle';
      return fit === 'universal';
    }

    _updatePaginationControlsFromTemplate(fragment) {
      const nextPagination = fragment.querySelector('.js-pagination');
      const nextBtn = nextPagination?.querySelector('.js-pagination__next-button[href]');
      if (nextPagination && nextBtn) {
        this.paginationContainer.innerHTML = nextPagination.innerHTML;
        this._refreshNextButton();
      } else {
        this._cleanupAfterLastPage();
      }
    }

    _refreshNextButton() {
      this.nextButton?.removeEventListener('click', this.clickHandler);
      this.nextButton = this.querySelector('.js-pagination__next-button');
      this.nextButton?.addEventListener('click', this.clickHandler);
    }

    _cleanupAfterLastPage() {
      this.intersectionObserver?.disconnect();
      clearInterval(this.nextButtonTimer);
      this.paginationContainer?.remove();
      this.querySelectorAll('.js-when-paginated-only').forEach(elem => elem.remove());
    }
  }

  customElements.define('infinite-scroll', InfiniteScroll);
}
